import { useEffect, useRef } from 'react';

import { sendHostActivity } from 'mastodon/actions/rooms';
import { useAppDispatch } from 'mastodon/store';

import {
  createHostActivityBeacon,
  HOST_ACTIVITY_TICK_MS,
} from '../host_activity';
import type { HostActivityBeacon } from '../host_activity';

export interface HostPresenceSignals {
  /** Is the host being heard in the room right now? */
  isSpeaking: boolean;
  /** Is the host publishing camera or screen share from this tab? */
  isStreaming: boolean;
}

/**
 * Reports the host as present while they are actually taking part in the room
 * (ticket 69): speaking, or publishing video. A no-op for everyone else — only
 * the host has a presence worth reporting, and the server refuses the call from
 * anyone else anyway.
 *
 * Note what is NOT here: no input listeners, no beat on entry, no beat on the
 * tab coming back into view. Under the reporter's definition ("away: inactive
 * voice or stream") none of those are participation, and a beat on any of them
 * would keep a silent host looking present indefinitely.
 */
export function useHostActivity(
  roomId: string,
  isHost: boolean,
  { isSpeaking, isStreaming }: HostPresenceSignals,
): void {
  const dispatch = useAppDispatch();
  // The dispatching send is held in a ref so a re-render cannot tear down and
  // rebuild the beacon, which would drop a pending beat each time.
  const sendRef = useRef<() => void>(() => undefined);
  // Read through a ref for the same reason: the streaming state changes while
  // the beacon is alive, and rebuilding it on every change would reset its gap.
  const streamingRef = useRef(isStreaming);
  const beaconRef = useRef<HostActivityBeacon | null>(null);

  sendRef.current = () => {
    void dispatch(sendHostActivity(roomId));
  };
  streamingRef.current = isStreaming;

  useEffect(() => {
    if (!isHost) {
      beaconRef.current = null;
      return;
    }

    const beacon = createHostActivityBeacon({
      send: () => {
        sendRef.current();
      },
      isStreaming: () => streamingRef.current,
    });

    beaconRef.current = beacon;

    const timer = window.setInterval(() => {
      beacon.tick();
    }, HOST_ACTIVITY_TICK_MS);

    // A stream that is already running when this mounts must report without
    // waiting out a tick.
    beacon.tick();

    return () => {
      beaconRef.current = null;
      window.clearInterval(timer);
    };
  }, [roomId, isHost]);

  useEffect(() => {
    if (!isHost || !isSpeaking) return;

    // Beat on the rising edge as well as noting it, so a host who comes back
    // and says one word clears the AFK flag immediately rather than at the
    // next tick.
    beaconRef.current?.noteVoice();
    beaconRef.current?.tick();
  }, [isHost, isSpeaking]);
}
